import { Directive, ElementRef, HostListener, Input } from '@angular/core';
import { DestroyBase } from '../../../../components/common/ng-destroy-base';
import { AppLocalizationService } from '../../../../services/AppLocalizationService';
import { EventBusService } from '../../../../services/event-bus.service';

/**
 * Numbers only directive
 */
//Based on: https://stackoverflow.com/questions/41465542/angular2-input-field-to-accept-only-numbers
@Directive({
  selector: '[numbersOnly]'
})
export class NumbersOnlyDirective extends DestroyBase {
  @Input() decimals = 2;
  private allowSign = true;
  private decimalSeparator = '.';

  previousValue = '';

  // --------------------------------------
  //  Regular expressions
  integerUnsigned = '^[0-9]*$';
  integerSigned = '^-?[0-9]+$';
  decimalUnsigned = '^[0-9]+(.[0-9]+)?$';
  decimalSigned = '^-?[0-9]+(.[0-9]+)?$';

  /**
   * Class constructor
   * @param hostElement
   */
  constructor(private hostElement: ElementRef, private appLocalization: AppLocalizationService, private eventBus: EventBusService) {
    super();
    this.eventBus.subscribeEvent('localeChanged', () => {
      this.applyLocale();
    });

    this.applyLocale();
  }

  private applyLocale() {
    this.decimalSeparator = this.appLocalization.getReportDesignerDecimalsSeparator();
  }

  /**
   * Event handler for host's change event
   * @param e
   */
  @HostListener('change', ['$event']) onChange(e) {
    this.validateValue(this.hostElement.nativeElement.value);
  }

  /**
   * Event handler for host's paste event
   * @param e
   */
  @HostListener('paste', ['$event']) onPaste(e) {
    // get and validate data from clipboard
    const value = e.clipboardData.getData('text/plain');
    this.validateValue(value);
    e.preventDefault();
  }

  /**
   * Event handler for host's keydown event
   * @param event
   */
  @HostListener('keydown', ['$event']) onKeyDown(e: KeyboardEvent) {
    const cursorPosition: number = e.target['selectionStart'];
    const selectionEnd: number = e.target['selectionEnd'];
    const originalValue: string = e.target['value'];
    const key: string = this.getName(e);
    const controlOrCommand = (e.ctrlKey === true || e.metaKey === true);
    const signExists = originalValue.includes('-');
    const separatorExists = originalValue.includes(this.decimalSeparator);

    //This is a simplified version, because some text could be selected
    //also we allow pasting without checking what/where
    //but we cover most "normal" user usage

    // allowed keys apart from numeric characters
    const allowedKeys = this.getAllowedKeys(signExists, cursorPosition, separatorExists);

    // when minus sign is allowed, add its
    // key to allowed key only when the
    // cursor is in the first position, and
    // first character is different from
    // decimal separator
    const firstCharacterIsSeparator = originalValue.startsWith(this.decimalSeparator);
    if (this.allowSign && !signExists && firstCharacterIsSeparator && cursorPosition == 0) {

      allowedKeys.push('-');
    }

    // allow some non-numeric characters
    if (allowedKeys.indexOf(key) != -1 ||
      // Allow: Ctrl+A and Command+A
      (key == 'a' && controlOrCommand) ||
      // Allow: Ctrl+C and Command+C
      (key == 'c' && controlOrCommand) ||
      // Allow: Ctrl+V and Command+V
      (key == 'v' && controlOrCommand) ||
      // Allow: Ctrl+X and Command+X
      (key == 'x' && controlOrCommand)) {
      // let it happen, don't do anything
      return;
    }

    // save value before keydown event
    this.previousValue = originalValue;

    // allow number characters only
    const isNumber = (new RegExp(this.integerUnsigned)).test(key);
    if (!isNumber) {
      e.preventDefault();
    }
    else if (separatorExists && this.decimals && cursorPosition > 0) {
      this.handleSeparator(originalValue, cursorPosition, selectionEnd, e);
    }
  }

  private handleSeparator(originalValue: string, cursorPosition: number, selectionEnd: number, e: KeyboardEvent) {
    let intermediateValue = originalValue;
    if (cursorPosition !== selectionEnd) {
      // text is selected, will be overwritten with new number, so let's calc what will be left
      intermediateValue = originalValue.substring(0, cursorPosition) + originalValue.substring(selectionEnd);
    }

    // check if we reached number of decimals allowed
    const separatorPosition = intermediateValue.indexOf(this.decimalSeparator);
    const typingAfterSeparator = cursorPosition > separatorPosition;
    if (typingAfterSeparator) {
      const parts = intermediateValue.split(this.decimalSeparator);
      const decimalAllowed = parts.length <= 1 || (parts.length > 1 && parts[1].length < this.decimals);
      if (!decimalAllowed) {
        e.preventDefault();
      }
    }
  }

  private getAllowedKeys(signExists: boolean, cursorPosition: number, separatorExists: boolean): string[] {
    const allowedKeys = [
      'Backspace', 'ArrowLeft', 'ArrowRight', 'Escape', 'Tab'
    ];

    // when decimals are allowed, add
    // decimal separator to allowed codes when
    // its position is not close to the the sign (-. and .-)
    const separatorIsCloseToSign = (signExists && cursorPosition <= 1);
    if (this.decimals && !separatorIsCloseToSign && !separatorExists) {

      if (this.decimalSeparator == '.') {
        allowedKeys.push('.');
      }
      else {
        allowedKeys.push(',');
      }
    }
    return allowedKeys;
  }

  /**
   * Test whether value is a valid number or not
   * @param value
   */
  validateValue(value: string): void {

    // choose the appropiate regular expression
    let regex: string;
    if (!this.decimals && !this.allowSign) {
      regex = this.integerUnsigned;
    }
    if (!this.decimals && this.allowSign) {
      regex = this.integerSigned;
    }
    if (this.decimals && !this.allowSign) {
      regex = this.decimalUnsigned;
    }
    if (this.decimals && this.allowSign) {
      regex = this.decimalSigned;
    }

    // when a numbers begins with a decimal separator,
    // fix it adding a zero in the beginning
    if (value.startsWith(this.decimalSeparator)) {
      value = 0 + value;
    }

    // when a numbers ends with a decimal separator,
    // fix it adding a zero in the end
    if (value.endsWith(this.decimalSeparator)) {
      value = value + 0;
    }

    // test number with regular expression, when
    // number is invalid, replace it with a zero
    const valid: boolean = (new RegExp(regex)).test(value);
    this.hostElement.nativeElement['value'] = valid ? value : 0;
  }

  /**
   * Get key's name
   * @param e
   */
  getName(e): string {
    if (e.key) {
      return e.key;
    } else if (e.keyCode && String.fromCharCode) {
      // for old browsers
      switch (e.keyCode) {
        case 8: return 'Backspace';
        case 9: return 'Tab';
        case 27: return 'Escape';
        case 37: return 'ArrowLeft';
        case 39: return 'ArrowRight';
        case 188: return ',';
        case 190: return '.';
        case 109: return '-'; // minus in numbpad
        case 173: return '-'; // minus in alphabet keyboard in firefox
        case 189: return '-'; // minus in alphabet keyboard in chrome
        default: return String.fromCharCode(e.keyCode);
      }
    }
  }
}
